type EmbedLayout = "inline" | "modal";
type EmbedView = "booking" | "gift-cards";

export type WidgetConfig = {
  bookingUrl: string;
  buttonLabel: string;
  forwardQueryParams: boolean;
  height: string;
  layout: EmbedLayout;
  minHeight: string;
  serviceId: string;
  targetSelector: string;
  tenantSlug: string;
  title: string;
  view: EmbedView;
};

export type WidgetScriptSource = {
  dataset: Partial<Record<string, string>>;
  src: string;
};

const mountedAttribute = "data-bookings-mounted";
const styleElementId = "bookings-embed-widget-style";
const defaultHeight = "760px";
const defaultMinHeight = "560px";

export function readWidgetConfig(source: WidgetScriptSource, pageUrl = globalThis.location?.href || "https://example.com/"): WidgetConfig {
  const tenantSlug = (source.dataset.tenantSlug || "").trim();
  const forwardQueryParams = normalizeBoolean(source.dataset.forwardQueryParams || source.dataset.forwardQuery, true);
  const serviceId = normalizeServiceId(source.dataset.serviceId || source.dataset.service || source.dataset.serviseId);
  const view = normalizeView(source.dataset.view || source.dataset.mode, source.dataset.giftCard || source.dataset.giftCards);
  const bookingUrlTemplate = view === "gift-cards"
    ? source.dataset.giftCardUrl || source.dataset.giftCardsUrl || source.dataset.giftCardUrlTemplate || source.dataset.giftCardsUrlTemplate || source.dataset.bookingUrl
    : source.dataset.bookingUrl;

  if (!isValidTenantSlug(tenantSlug)) {
    throw new Error("Bookings embed requires a valid data-tenant-slug value.");
  }

  return {
    bookingUrl: buildBookingUrl({
      bookingBaseUrl: source.dataset.bookingBaseUrl || source.dataset.baseUrl,
      bookingUrlTemplate,
      forwardQueryParams,
      pageUrl,
      serviceId,
      scriptSrc: source.src,
      tenantSlug,
      view
    }),
    buttonLabel: normalizeText(source.dataset.buttonLabel, view === "gift-cards" ? "Buy a Prepaid Ritual Pass" : "Book appointment"),
    forwardQueryParams,
    height: normalizeCssLength(source.dataset.height, defaultHeight),
    layout: normalizeLayout(source.dataset.layout),
    minHeight: normalizeCssLength(source.dataset.minHeight, defaultMinHeight),
    serviceId,
    targetSelector: (source.dataset.target || "").trim(),
    tenantSlug,
    title: normalizeText(source.dataset.title, view === "gift-cards" ? "Buy a Prepaid Ritual Pass" : "Book an appointment"),
    view
  };
}

export function buildBookingUrl(options: {
  bookingBaseUrl?: string;
  bookingUrlTemplate?: string;
  forwardQueryParams?: boolean;
  pageUrl: string;
  serviceId?: string;
  scriptSrc: string;
  tenantSlug: string;
  view?: EmbedView;
}): string {
  if (!isValidTenantSlug(options.tenantSlug)) {
    throw new Error("Tenant slug must use lowercase letters, numbers, and hyphens.");
  }

  const view = options.view || "booking";
  const tenantPathSegment = encodeURIComponent(options.tenantSlug);
  const pageUrl = new URL(options.pageUrl, "https://example.com/");
  const forwardedServiceId = options.forwardQueryParams === false ? "" : getForwardedServiceId(pageUrl.searchParams);
  const serviceId = forwardedServiceId || normalizeServiceId(options.serviceId);
  const servicePathSegment = serviceId ? encodeURIComponent(serviceId) : "";
  const widgetPath = view === "gift-cards"
    ? `book/${tenantPathSegment}/gift-cards`
    : `book/${tenantPathSegment}`;
  const viewPathSegment = view === "gift-cards" ? "gift-cards" : "";
  const bookingUrlTemplate = options.bookingUrlTemplate?.trim();
  const url = bookingUrlTemplate
    ? new URL(
      bookingUrlTemplate
        .replace(/\{tenantSlug\}/g, tenantPathSegment)
        .replace(/\{serviceId\}/g, servicePathSegment)
        .replace(/\{widgetPath\}/g, widgetPath)
        .replace(/\{view\}/g, viewPathSegment),
      options.pageUrl
    )
    : new URL(`/${widgetPath}`, new URL((options.bookingBaseUrl || options.scriptSrc || options.pageUrl).trim(), options.pageUrl).origin);

  if (options.forwardQueryParams !== false) {
    copySearchParams(pageUrl.searchParams, url.searchParams);
  }

  if (serviceId && !bookingUrlTemplate?.includes("{serviceId}") && !url.searchParams.has("serviceId") && !url.searchParams.has("service")) {
    url.searchParams.set("serviceId", serviceId);
  }

  url.searchParams.set("embed", "1");
  return url.toString();
}

export function isValidTenantSlug(value: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

export function isValidServiceId(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(value);
}

export function mountBookingWidget(script = getCurrentWidgetScript()): void {
  if (!script || script.hasAttribute(mountedAttribute)) {
    return;
  }

  injectStyles(document);
  script.setAttribute(mountedAttribute, "true");

  let config: WidgetConfig;
  try {
    config = readWidgetConfig(script, window.location.href);
  } catch (error) {
    renderError(script, error instanceof Error ? error.message : "Bookings embed could not load.");
    return;
  }

  const root = document.createElement("div");
  root.className = `bookings-embed-widget bookings-embed-widget--${config.layout}`;
  root.setAttribute("data-bookings-widget", config.tenantSlug);
  root.setAttribute("data-bookings-view", config.view);

  if (config.layout === "modal") {
    root.appendChild(createModalLauncher(config));
  } else {
    root.appendChild(createFrameShell(config));
  }

  const target = resolveTarget(script, config.targetSelector);
  if (config.targetSelector && !target) {
    return;
  }

  if (target) {
    target.appendChild(root);
  } else {
    script.insertAdjacentElement("afterend", root);
  }
}

function getCurrentWidgetScript(): HTMLScriptElement | null {
  const currentScript = document.currentScript instanceof HTMLScriptElement ? document.currentScript : null;

  if (currentScript?.dataset.tenantSlug) {
    return currentScript;
  }

  return Array.from(document.querySelectorAll<HTMLScriptElement>("script[data-tenant-slug]"))
    .find((candidate) => !candidate.hasAttribute(mountedAttribute)) || null;
}

function resolveTarget(script: HTMLScriptElement, selector: string): Element | null {
  if (!selector) {
    return null;
  }

  const target = document.querySelector(selector);
  if (!target) {
    renderError(script, `Bookings embed target was not found: ${selector}`);
  }

  return target;
}

function createFrameShell(config: WidgetConfig): HTMLElement {
  const shell = document.createElement("div");
  shell.className = "bookings-embed-widget__frame-shell";
  shell.style.height = config.height;
  shell.style.minHeight = config.minHeight;
  shell.appendChild(createFrame(config));
  return shell;
}

function createFrame(config: WidgetConfig): HTMLIFrameElement {
  const iframe = document.createElement("iframe");
  iframe.className = "bookings-embed-widget__frame";
  iframe.src = config.bookingUrl;
  iframe.title = config.title;
  iframe.loading = "lazy";
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  iframe.allow = "payment *";
  return iframe;
}

function createModalLauncher(config: WidgetConfig): HTMLElement {
  const launcher = document.createElement("div");
  launcher.className = "bookings-embed-widget__modal-launcher";

  const button = document.createElement("button");
  button.className = "bookings-embed-widget__button";
  button.type = "button";
  button.textContent = config.buttonLabel;

  const overlay = document.createElement("div");
  overlay.className = "bookings-embed-widget__overlay";
  overlay.hidden = true;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", config.title);

  const panel = document.createElement("div");
  panel.className = "bookings-embed-widget__modal-panel";

  const closeButton = document.createElement("button");
  closeButton.className = "bookings-embed-widget__close";
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close booking");
  closeButton.textContent = "Close";

  const frameShell = createFrameShell(config);
  frameShell.classList.add("bookings-embed-widget__frame-shell--modal");
  panel.append(closeButton, frameShell);
  overlay.appendChild(panel);

  const close = () => {
    overlay.hidden = true;
    document.body.classList.remove("bookings-embed-widget--modal-open");
    button.focus();
  };

  button.addEventListener("click", () => {
    overlay.hidden = false;
    document.body.classList.add("bookings-embed-widget--modal-open");
    closeButton.focus();
  });
  closeButton.addEventListener("click", close);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (!overlay.hidden && event.key === "Escape") {
      close();
    }
  });

  launcher.append(button, overlay);
  return launcher;
}

function renderError(script: HTMLScriptElement, message: string): void {
  const error = document.createElement("p");
  error.className = "bookings-embed-widget__error";
  error.textContent = message;
  script.insertAdjacentElement("afterend", error);
}

function normalizeLayout(value: string | undefined): EmbedLayout {
  return value?.trim().toLowerCase() === "modal" ? "modal" : "inline";
}

function normalizeView(value: string | undefined, giftCardFlag: string | undefined): EmbedView {
  if (normalizeBoolean(giftCardFlag, false)) {
    return "gift-cards";
  }

  const normalized = value?.trim().toLowerCase().replace(/[_\s]+/g, "-") || "";
  return ["gift-card", "gift-cards", "giftcard", "giftcards"].includes(normalized) ? "gift-cards" : "booking";
}

function normalizeBoolean(value: string | undefined, fallback: boolean): boolean {
  const normalized = value?.trim().toLowerCase() || "";
  if (["true", "1", "yes", "on"].includes(normalized)) {
    return true;
  }

  if (["false", "0", "no", "off"].includes(normalized)) {
    return false;
  }

  return fallback;
}

function normalizeServiceId(value: string | undefined): string {
  const normalized = value?.trim() || "";
  if (!normalized) {
    return "";
  }

  if (!isValidServiceId(normalized)) {
    throw new Error("Bookings embed data-service-id must use letters, numbers, underscores, or hyphens.");
  }

  return normalized;
}

function getForwardedServiceId(params: URLSearchParams): string {
  const serviceId = params.get("serviceId") || params.get("service") || params.get("serviseId") || "";
  const normalized = serviceId.trim();
  return isValidServiceId(normalized) ? normalized : "";
}

function copySearchParams(source: URLSearchParams, target: URLSearchParams): void {
  source.forEach((value, key) => {
    target.set(key, value);
  });
}

function normalizeCssLength(value: string | undefined, fallback: string): string {
  const normalized = value?.trim() || "";

  if (/^\d+$/.test(normalized)) {
    return `${normalized}px`;
  }

  if (/^\d+(?:\.\d+)?(?:px|rem|vh|vw|%)$/.test(normalized)) {
    return normalized;
  }

  if (isSafeCssCalcLength(normalized)) {
    return normalized;
  }

  return fallback;
}

function isSafeCssCalcLength(value: string): boolean {
  if (!/^calc\(.+\)$/i.test(value)) {
    return false;
  }

  if (/[;"'{}<>\\]/.test(value)) {
    return false;
  }

  if (!/\d+(?:\.\d+)?(?:px|rem|vh|vw|%)/i.test(value)) {
    return false;
  }

  const remainder = value
    .replace(/calc/gi, "")
    .replace(/\d+(?:\.\d+)?(?:px|rem|vh|vw|%)/gi, "")
    .replace(/\d+(?:\.\d+)?/g, "")
    .replace(/[\s+\-*/().]/g, "");

  return remainder.length === 0;
}

function normalizeText(value: string | undefined, fallback: string): string {
  return value?.trim() || fallback;
}

function injectStyles(ownerDocument: Document): void {
  if (ownerDocument.getElementById(styleElementId)) {
    return;
  }

  const style = ownerDocument.createElement("style");
  style.id = styleElementId;
  style.textContent = `
.bookings-embed-widget,
.bookings-embed-widget * {
  box-sizing: border-box;
}

.bookings-embed-widget {
  color: #17201c;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  width: 100%;
}

.bookings-embed-widget__frame-shell {
  background: #ffffff;
  border: 1px solid #ddd8cd;
  border-radius: 8px;
  box-shadow: 0 18px 44px rgba(23, 32, 28, 0.12);
  max-height: 100vh;
  overflow: hidden;
  width: 100%;
}

.bookings-embed-widget__frame {
  border: 0;
  display: block;
  height: 100%;
  width: 100%;
}

.bookings-embed-widget__button {
  background: #1f6f68;
  border: 0;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  min-height: 44px;
  padding: 10px 18px;
}

.bookings-embed-widget__button:hover,
.bookings-embed-widget__button:focus-visible {
  background: #185a55;
}

.bookings-embed-widget__overlay {
  align-items: center;
  background: rgba(23, 32, 28, 0.72);
  display: grid;
  inset: 0;
  justify-items: center;
  padding: 18px;
  position: fixed;
  z-index: 2147483000;
}

.bookings-embed-widget__overlay[hidden] {
  display: none;
}

.bookings-embed-widget__modal-panel {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
  display: grid;
  gap: 10px;
  max-height: min(900px, calc(100vh - 36px));
  max-width: 1060px;
  padding: 10px;
  width: min(1060px, 100%);
}

.bookings-embed-widget__frame-shell--modal {
  height: min(820px, calc(100vh - 104px)) !important;
  min-height: 520px !important;
}

.bookings-embed-widget__close {
  align-self: start;
  background: #f5f2ea;
  border: 1px solid #ddd8cd;
  border-radius: 6px;
  color: #17201c;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  justify-self: end;
  min-height: 38px;
  padding: 8px 12px;
}

.bookings-embed-widget__error {
  background: #fff2ed;
  border: 1px solid #d16b4f;
  border-radius: 8px;
  color: #7e2d1c;
  font: 14px/1.4 Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  margin: 10px 0;
  padding: 10px 12px;
}

body.bookings-embed-widget--modal-open {
  overflow: hidden;
}
`;
  ownerDocument.head.appendChild(style);
}

if (typeof document !== "undefined") {
  mountBookingWidget();
}
